// src/context/Adviser/Utils/bulkUploadAdviserLicenseCertificates.js

import Papa from "papaparse";
import {
  collection,
  doc,
  getDocs,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "../../../../../backend/firebaseConfig";
import {
  COMPETENT_ADVISER_TEST_FIELDS,
  DEFAULT_COMPETENT_ADVISER_TEST_KEYS,
  getEmptyLicenseCertificates,
  LICENSE_CERTIFICATE_SECTIONS,
} from "./LicenseCertificateFields";


const ADVISERS_PATH = ["Authorized Members", "Advisers", "Data"];

const normaliseKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/\+/g, "and")
    .replace(/²/g, "2")
    .replace(/[^a-z0-9]/g, "");

const normaliseEmail = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const NA_VALUES = new Set([
  "",
  "n/a",
  "na",
  "n.a",
  "not applicable",
  "notapplicable",
  "-",
]);

const pad = (value) => String(value).padStart(2, "0");

const fixTwoDigitYear = (year) => {
  const numericYear = Number(year);

  if (String(year).length === 4) return numericYear;

  return numericYear <= 50 ? 2000 + numericYear : 1900 + numericYear;
};

export const toIsoDateOnlyFromImport = (value) => {
  if (value === null || value === undefined) return "";

  const raw = String(value).trim();

  if (!raw) return "";

  const normalised = raw.toLowerCase().replace(/\s/g, "");

  if (NA_VALUES.has(normalised)) return "N/A";

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw;
  }

  const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);

  if (slashMatch) {
    const [, day, month, year] = slashMatch;
    const fullYear = fixTwoDigitYear(year);

    return `${fullYear}-${pad(month)}-${pad(day)}`;
  }

  const dashMatch = raw.match(/^(\d{1,2})-(\d{1,2})-(\d{2}|\d{4})$/);

  if (dashMatch) {
    const [, day, month, year] = dashMatch;
    const fullYear = fixTwoDigitYear(year);

    return `${fullYear}-${pad(month)}-${pad(day)}`;
  }

  return raw;
};

const cleanText = (value) => {
  const raw = String(value || "").trim();

  if (!raw) return "";

  const normalised = raw.toLowerCase().replace(/\s/g, "");

  if (NA_VALUES.has(normalised)) return "N/A";

  return raw;
};

const normaliseSpsAccreditedBody = (value) => {
  const raw = String(value || "").trim().toUpperCase();

  if (!raw) return "";
  if (raw === "NA") return "N/A";

  if (["LIBF", "CII", "CISI", "N/A"].includes(raw)) {
    return raw;
  }

  return raw;
};

const getDateFieldKeys = () => {
  const keys = new Set();

  LICENSE_CERTIFICATE_SECTIONS.forEach((section) => {
    if (section.key === "competentAdviser") {
      COMPETENT_ADVISER_TEST_FIELDS.forEach((field) => {
        if (field.type === "date") {
          keys.add(`${section.key}.${field.key}`);
        }
      });

      return;
    }

    section.fields.forEach((field) => {
      if (field.type === "date") {
        keys.add(`${section.key}.${field.key}`);
      }
    });
  });

  return keys;
};

const DATE_FIELD_KEYS = getDateFieldKeys();

const COLUMN_MAP = {
  advisername: {
    section: "_meta",
    field: "name",
  },
  adviseremailaddress: {
    section: "_meta",
    field: "email",
  },
  adviseremail: {
    section: "_meta",
    field: "email",
  },
  emailaddress: {
    section: "_meta",
    field: "email",
  },
  email: {
    section: "_meta",
    field: "email",
  },

  dplname: {
    section: "dplCertificate",
    field: "dplName",
  },
  dplregistrationnumber: {
    section: "dplCertificate",
    field: "dplRegistrationNumber",
  },
  dplexpirydate: {
    section: "dplCertificate",
    field: "dplExpiryDate",
  },

  spsaccreditedbody: {
    section: "spsCertificate",
    field: "spsAccreditedBody",
  },
  spsexpirydate: {
    section: "spsCertificate",
    field: "spsExpiryDate",
  },

  fitandproperexpirydate: {
    section: "fitAndProper",
    field: "fitAndProperExpiryDate",
  },

  moneylaunderingregulationspdp: {
    section: "competentAdviser",
    field: "moneyLaunderingRegulationsPdp",
  },
  gdprawarenesspdp: {
    section: "competentAdviser",
    field: "gdprAwarenessPdp",
  },

  complainthandlingpdp: {
    section: "competentAdviser",
    field: "complianceReadingPdp",
  },
  complianthandlingpdp: {
    section: "competentAdviser",
    field: "complianceReadingPdp",
  },
  compliancereadingpdp: {
    section: "competentAdviser",
    field: "complianceReadingPdp",
  },

  mortgageproductsandregulationspdp: {
    section: "competentAdviser",
    field: "mortgageAndProtectionPdp",
  },
  mortgageandprotectionpdp: {
    section: "competentAdviser",
    field: "mortgageAndProtectionPdp",
  },

  protectiontest: {
    section: "competentAdviser",
    field: "protectionTest",
  },

  pensionswitchanddefinedcontributionpdp: {
    section: "competentAdviser",
    field: "pensionSwitchAndDefinedContributionsPdp",
  },
  pensionswitchanddefinedcontributionspdp: {
    section: "competentAdviser",
    field: "pensionSwitchAndDefinedContributionsPdp",
  },

  pensionswitchanddefinedcontributionpdp: {
    section: "competentAdviser",
    field: "pensionSwitchAndDefinedContributionsPdp",
  },

  pensionswitchanddefinedcontributionspdp: {
    section: "competentAdviser",
    field: "pensionSwitchAndDefinedContributionsPdp",
  },

  regulatedretailinvestmentadviserreevaluationrriartest: {
    section: "competentAdviser",
    field: "rriarTest",
  },

  regulatedretailinvestmentadviserrerevaluationrriartest: {
    section: "competentAdviser",
    field: "rriarTest",
  },

  regulatedretailinvestmentadviserrriartest: {
    section: "competentAdviser",
    field: "rriarTest",
  },

  rriartest: {
    section: "competentAdviser",
    field: "rriarTest",
  },

  vcttest: {
    section: "competentAdviser",
    field: "vct",
  },
  vct: {
    section: "competentAdviser",
    field: "vct",
  },

  longtermcaretest: {
    section: "competentAdviser",
    field: "longTermCareTest",
  },

  equityreleasetest: {
    section: "competentAdviser",
    field: "equityReleaseTest",
  },

  consumerdutytest: {
    section: "competentAdviser",
    field: "consumerDutyTest",
  },

  appointmentdate: {
    section: "tc",
    field: "appointmentDate",
  },

  precassignoff: {
    section: "tc",
    field: "preCASSignOff",
  },
  precassignoffdate: {
    section: "tc",
    field: "preCASSignOff",
  },

  lastocameeting: {
    section: "tc",
    field: "lastOcaMeeting",
  },

  nextocameeting: {
    section: "tc",
    field: "nextOcaMeeting",
  },
};

const getGoogleSheetIdFromUrl = (sheetUrl) => {
  const match = String(sheetUrl || "").match(/\/spreadsheets\/d\/([^/]+)/);
  return match?.[1] || "";
};

const getGidFromUrl = (sheetUrl) => {
  const match = String(sheetUrl || "").match(/[?&#]gid=(\d+)/);
  return match?.[1] || "0";
};

export const getGoogleSheetCsvExportUrl = (sheetUrl) => {
  const sheetId = getGoogleSheetIdFromUrl(sheetUrl);
  const gid = getGidFromUrl(sheetUrl);

  if (!sheetId) {
    throw new Error("Invalid Google Sheet URL.");
  }

  return `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv&gid=${gid}`;
};

const findHeaderRowIndex = (rows) => {
  return rows.findIndex((row) => {
    const joined = row.map(normaliseKey).join("|");

    return (
      joined.includes("adviseremailaddress") ||
      joined.includes("adviseremail") ||
      joined.includes("emailaddress")
    );
  });
};

const buildHeader = (rows, headerRowIndex) => {
  const headerRow = rows[headerRowIndex] || [];
  const previousRow = rows[headerRowIndex - 1] || [];

  return headerRow.map((cell, index) => {
    const current = String(cell || "").trim();
    const previous = String(previousRow[index] || "").trim();

    return current || previous;
  });
};

const setMappedValue = (payload, mapping, rawValue) => {
  if (!payload?.[mapping.section]) return;

  if (
    mapping.section === "spsCertificate" &&
    mapping.field === "spsAccreditedBody"
  ) {
    payload.spsCertificate.spsAccreditedBody =
      normaliseSpsAccreditedBody(rawValue);
    return;
  }

  const fieldPath = `${mapping.section}.${mapping.field}`;

  payload[mapping.section][mapping.field] = DATE_FIELD_KEYS.has(fieldPath)
    ? toIsoDateOnlyFromImport(rawValue)
    : cleanText(rawValue);
};

const parseRowsToPayloads = (rows) => {
  const headerRowIndex = findHeaderRowIndex(rows);

  if (headerRowIndex < 0) {
    throw new Error(
      "Could not find the header row. Please check the sheet includes Adviser Email Address.",
    );
  }

  const headers = buildHeader(rows, headerRowIndex).map(normaliseKey);
  const dataRows = rows.slice(headerRowIndex + 1);

  const payloads = [];
  const skipped = [];

  dataRows.forEach((row, rowIndex) => {
    const rowNumber = headerRowIndex + rowIndex + 2;
    const payload = getEmptyLicenseCertificates();

    payload.competentAdviser = {
      ...(payload.competentAdviser || {}),
      enabledTests: DEFAULT_COMPETENT_ADVISER_TEST_KEYS,
    };

    let adviserEmail = "";

    headers.forEach((headerKey, columnIndex) => {
      const mapping = COLUMN_MAP[headerKey];

      if (!mapping) return;

      const rawValue = row[columnIndex];

      if (mapping.section === "_meta" && mapping.field === "email") {
        adviserEmail = normaliseEmail(rawValue);
        return;
      }

      setMappedValue(payload, mapping, rawValue);
    });

    const hasAnyData =
      Object.values(payload.dplCertificate || {}).some(Boolean) ||
      Object.values(payload.spsCertificate || {}).some(Boolean) ||
      Object.values(payload.fitAndProper || {}).some(Boolean) ||
      Object.values(payload.competentAdviser || {}).some((value) => {
        if (Array.isArray(value)) return false;
        return Boolean(value);
      }) ||
      Object.values(payload.tc || {}).some(Boolean);

    if (!adviserEmail && !hasAnyData) {
      return;
    }

    if (!adviserEmail) {
      skipped.push({
        rowNumber,
        reason: "Missing adviser email address.",
      });
      return;
    }

    payloads.push({
      rowNumber,
      adviserEmail,
      payload,
    });
  });

  return {
    payloads,
    skipped,
  };
};

const getAdviserIdMap = async () => {
  const advisersSnap = await getDocs(collection(db, ...ADVISERS_PATH));
  const adviserIdMap = new Map();

  advisersSnap.forEach((adviserDoc) => {
    const data = adviserDoc.data() || {};
    if (data.isIndroducerAR === true) return;

    adviserIdMap.set(normaliseEmail(adviserDoc.id), adviserDoc.id);

    if (data.email) {
      adviserIdMap.set(normaliseEmail(data.email), adviserDoc.id);
    }

    if (data.Email) {
      adviserIdMap.set(normaliseEmail(data.Email), adviserDoc.id);
    }
  });

  return adviserIdMap;
};

const buildPreviewRows = ({ matchedRows, notFound }) => {
  const matchedPreviewRows = matchedRows.map((item) => ({
    rowNumber: item.rowNumber,
    adviserEmail: item.adviserEmail,
    adviserId: item.adviserId,
    status: "Matched",
    payload: item.payload,
  }));

  const notFoundPreviewRows = notFound.map((item) => ({
    rowNumber: item.rowNumber,
    adviserEmail: item.adviserEmail,
    adviserId: "",
    status: "Not found",
    payload: item.payload,
  }));

  return [...matchedPreviewRows, ...notFoundPreviewRows].sort(
    (a, b) => a.rowNumber - b.rowNumber,
  );
};
export const previewAdviserLicenseCertificatesFromCsvText = async (
  csvText,
  options = {},
) => {
  const parsed = Papa.parse(csvText, {
    skipEmptyLines: false,
  });

  if (parsed.errors?.length) {
    console.warn("CSV parse warnings:", parsed.errors);
  }

  const rows = parsed.data || [];
  const { payloads, skipped } = parseRowsToPayloads(rows);
  const adviserIdMap = await getAdviserIdMap();

  const matchedRows = [];
  const notFound = [];

  payloads.forEach((item) => {
    const adviserId = adviserIdMap.get(item.adviserEmail);

    if (!adviserId) {
      notFound.push({
        ...item,
        reason: "No matching adviser found in Firestore.",
      });
      return;
    }

    matchedRows.push({
      ...item,
      adviserId,
    });
  });

  const previewRows = buildPreviewRows({
    matchedRows,
    notFound,
  });

  return {
    sourceUrl: options.sourceUrl || "",
    sourceType: options.sourceType || "csv",
    totalRows: payloads.length + skipped.length,
    parsedRows: payloads.length,
    matched: matchedRows.length,
    skipped,
    notFound,
    matchedRows,
    previewRows,
    written: 0,
  };
};

export const previewAdviserLicenseCertificatesFromGoogleSheet = async (
  sheetUrl,
) => {
  const csvUrl = getGoogleSheetCsvExportUrl(sheetUrl);
  const response = await fetch(csvUrl);

  if (!response.ok) {
    throw new Error(
      "Could not read the Google Sheet. Make sure it is shared publicly, published, or use the CSV upload option instead.",
    );
  }

  const csvText = await response.text();

  return previewAdviserLicenseCertificatesFromCsvText(csvText, {
    sourceUrl: sheetUrl,
    sourceType: "google-sheet",
  });
};

const commitInChunks = async (items, source = {}) => {
  const chunkSize = 450;
  let written = 0;

  for (let index = 0; index < items.length; index += chunkSize) {
    const chunk = items.slice(index, index + chunkSize);
    const batch = writeBatch(db);

    chunk.forEach(({ adviserId, adviserEmail, payload, rowNumber }) => {
      const licenseCertificatesRef = doc(
        db,
        ...ADVISERS_PATH,
        adviserId,
        "License Certificates",
        "Data",
      );

      batch.set(
        licenseCertificatesRef,
        {
          ...payload,
          adviserEmail,
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.email || "",
          bulkImport: {
            importedAt: serverTimestamp(),
            importedBy: auth.currentUser?.email || "",
            sourceUrl: source.sourceUrl || "",
            sourceType: source.sourceType || "",
            sourceRowNumber: rowNumber,
          },
        },
        { merge: true },
      );
    });

    await batch.commit();
    written += chunk.length;
  }

  return written;
};

export const commitAdviserLicenseCertificatesPreview = async (preview) => {
  const matchedRows = Array.isArray(preview?.matchedRows)
    ? preview.matchedRows
    : [];

  if (!matchedRows.length) {
    throw new Error("There are no matched adviser rows to upload.");
  }

  const written = await commitInChunks(matchedRows, {
    sourceUrl: preview?.sourceUrl || "",
    sourceType: preview?.sourceType || "",
  });

  return {
    ...preview,
    written,
  };
};

// Optional: keep old direct import functions if anything else uses them.
export const bulkUploadAdviserLicenseCertificatesFromCsvText = async (
  csvText,
  options = {},
) => {
  const preview = await previewAdviserLicenseCertificatesFromCsvText(
    csvText,
    options,
  );

  return commitAdviserLicenseCertificatesPreview(preview);
};

export const bulkUploadAdviserLicenseCertificatesFromGoogleSheet = async (
  sheetUrl,
) => {
  const preview =
    await previewAdviserLicenseCertificatesFromGoogleSheet(sheetUrl);

  return commitAdviserLicenseCertificatesPreview(preview);
};
